import { ACTION_ARGS, Arg, Metadata, pick } from '../../../../utils/spds/metadata';
import { Animated } from './animated.gjs';
import { OPEN_CLOSE_ANIMATION_DURATION } from '../../../../utils/spds/constants/animations';
import { buildWaiter } from '@ember/test-waiters';
import { compose } from '../../../../utils/spds/capabilities/helpers';
import { exists } from '../../../../utils/spds/component-validations/helpers';
import { isCloseKey } from '../../../../utils/keyboard-nav-support';
import { tracked } from '@glimmer/tracking';

const METADATA = Metadata.define({
  description: `
Gives a component an open/close lifecycle. It makes no assumption about what element is being dismissed, so a dialog, a
banner, and an in-flow panel can all share it. Applied as a capability: \`class Foo extends compose(Super, Dismissible)\`.
Composes \`Animated\`, so anything dismissible also gets \`@animated\` and its exit transition handling.

### Opening
A dismissible component is born open: \`isVisible\` starts true, so the open classes are on the element from its first
render. \`show\` then runs from \`setup\`, when the element is in the DOM — it fires \`onOpen\`, hands off to
\`showElement\` for whatever the subclass has to do to actually display, settles the opening test waiter, and fires
\`onShow\`. A consumer that measures or moves focus on \`onShow\` therefore gets an element that is genuinely showing.

### The lifecycle
A dismissal request enters through \`beforeClose\`, which consults the consumer's \`onBeforeClose\` hook and continues to
\`close\` only if that hook answers truthy. \`close\` then drops \`isVisible\` — which removes the \`is-visible\` class via
\`classesToMap\`, letting the stylesheet start its exit transition — waits that transition out, and hands off to
\`dismissElement\`.

Both entry points are latched. A request that arrives while an earlier one is still waiting on \`onBeforeClose\`, or while
the exit transition is playing, is dropped rather than queued. Without that, a repeated dismissal raises a second discard
prompt behind the first, or starts a second close that leaves the first test waiter unsettled and fires \`onClose\` twice.

### Hooks for subclasses
Three overridable methods cover the parts that differ per subclass:

* \`showElement\`: displays the element, awaited between \`onOpen\` and \`onShow\`. A no-op by default, since a banner is
showing the moment its class is on
* \`beginDismiss\`: runs as the exit transition starts, while the element is still mounted and focusable. Override it to
move focus out, or otherwise act on the element before it stops accepting interaction
* \`dismissElement\`: runs once the transition has played, and is responsible for settling the closing waiter and reporting
the dismissal — through \`settleToken\` and \`reportClose\`, which make both safe to reach twice. It is a no-op by default
because there is no single element every subclass renders

\`handleEscape\` is defined here as well, but left for a subclass to attach. A native modal dialog already raises \`cancel\`
for Escape and a banner wants no keyboard behavior at all, so only the cases that need it — non-modal dialogs — wire it
to a \`keydown\` listener.

### Consumer hooks
* \`onBeforeClose\`: catches a dismissal request and can block it. A truthy return continues the dismissal; a falsey one
leaves the component open
* \`onClose\`: custom behavior once the component has closed, such as interaction tracking or flipping the parent
\`{{#if}}\` that destroys this component
* \`onOpen\` / \`onShow\`: custom behavior as the component opens
  `,
  extends: [pick(ACTION_ARGS, ['onClose', 'onBeforeClose', 'onOpen'])],
  meta: {
    onShow: Arg.action({
      description: 'Hook to perform custom behavior when the component is shown',
    }),
    renderedWithPromise: Arg.boolean({
      default: false,
      description:
        'Whether the component is rendered with a promise, which leaves the opening waiter to the caller; only used by the global modal service',
    }),
    variantName: Arg.string({
      description:
        'Allows wrapping components to provide a custom component name for argument validation messages',
    }),
  },
  moduleUrl: import.meta.url,
  name: 'Dismissible',
});

/**
 * Capability adding an open/close lifecycle to any base-derived component.
 *
 * Arguments
 * @argument {function} onBeforeClose gives a hook where dismissal can be interrupted by custom behavior
 * @argument {function} onClose gives a hook to perform custom behavior when the component closes
 * @argument {function} onOpen gives a hook to perform custom behavior when the component opens
 * @argument {function} onShow gives a hook to perform custom behavior when the component is shown
 * @argument {boolean} renderedWithPromise indicates whether the component is being rendered with a promise, which requires special handling for waiters; only used by global modal service
 * @argument {string} variantName allows for wrapping components to provide a custom component name for argument validation messages
 *
 * @property {boolean} isVisible drives the `is-visible` class the subclass stylesheet transitions on; starts true so
 * the component is open from its first render
 */
export const Dismissible = Super =>
  class Dismissible extends compose(Super, Animated) {
    static metadata = this.setMetadata(METADATA, import.meta.url);

    token;

    // A dismissal request is waiting on `onBeforeClose`. Cleared when it answers, so a hook that
    // blocks the dismissal leaves the component dismissable. Without it a repeated request raises
    // a second discard prompt behind the first, since nothing is latched until the answer is in.
    #awaitingBeforeClose = false;

    // Latched for the length of the exit transition, during which the element is still mounted and
    // still listening. Without it a second dismissal starting mid-transition overwrites `token`, so
    // the first waiter never settles and `onClose` fires more than once. It is also what tells an
    // open sequence still in flight to stand down, since a dismissal takes ownership of `token`.
    // One-way on purpose: nothing reopens a dismissed component in place.
    #isClosing = false;

    // Separate from `#isClosing`, which is set as a dismissal begins. This records that one has been
    // reported, so the paths that can each reach the consumer — a native `close` event and the
    // handoff at the end of `close` — cannot both report the same dismissal.
    #hasReportedClose = false;

    // Born open. A dismissible component is rendered at the point it should already be showing, so
    // the flip belongs before the first render rather than in `setup`: a tracked change made from
    // inside the `setupComponent` modifier revalidates in a pass after `afterRender`, and `onShow`
    // would then run against an element not yet carrying its open classes.
    //
    // A subclass whose entry animation needs somewhere to travel from declares that travel as a
    // `@starting-style` in its own stylesheet rather than withholding this class — see the width
    // animation in `Spds::Panel::Collapsible` and the toast's entry transform. Both stay born open.
    @tracked isVisible = true;

    /**
     * Tracks the exit transition the modal stylesheets share. A subclass whose stylesheet
     * transitions over a different duration overrides this.
     */
    get animationDuration() {
      return OPEN_CLOSE_ANIMATION_DURATION;
    }

    get classesToMap() {
      return [...super.classesToMap, 'isVisible'];
    }

    get componentName() {
      return this.args.variantName ?? this.constructor.metadata.component;
    }

    get hasOnBeforeClose() {
      return exists(this.args.onBeforeClose);
    }

    /**
     * Whether a dismissal has taken over. Exposed for a subclass whose `showElement` has an async
     * gap of its own to re-check across: displaying an element after it has been dismissed strands
     * it on screen, since `close` is latched against running a second time.
     */
    get isClosing() {
      return this.#isClosing;
    }

    /**
     * @constructor set this.waiter to a custom waiter for this instance
     * using this.constructor.name and the generated uniqueIdentifier from the core/base class
     */
    constructor() {
      super(...arguments);

      this.waiter = buildWaiter(
        `@ember-simplepractice/${this.constructor.name}/${this.uniqueIdentifier}`
      );

      if (!this.args.renderedWithPromise) {
        this.token = this.waiter.beginAsync();
      }
    }

    /**
     * function that checks return from onBeforeClose handler to see if dismissal should continue
     * defaults to allowing it when no handler is provided
     */
    async #beforeClose(evt) {
      return this.hasOnBeforeClose ? Promise.resolve(this.args.onBeforeClose(evt)) : true;
    }

    /**
     * Runs as the exit transition starts, while the element is still mounted and focusable.
     * No-op by default. Subclasses that need to move focus out, or otherwise act on the element
     * before it stops accepting interaction, override this.
     */
    beginDismiss() {}

    /**
     * Dismisses the underlying element once the exit transition has played. Responsible for
     * settling the closing waiter and reporting the dismissal, through `settleToken` and
     * `reportClose` rather than by hand. No-op by default because there is no one element every
     * subclass renders: `Spds::Core::Modal` closes its dialog and lets the native `close` event do
     * both, while a subclass rendering plain markup has to do them itself.
     */
    dismissElement() {}

    /**
     * Shows the underlying element once the component has been marked open. Counterpart to
     * `dismissElement`, and a no-op for the same reason: a banner is showing the moment its class
     * is on, while `Spds::Core::Modal` has a `showModal` call to sequence against.
     */
    async showElement() {}

    /**
     * Settles the outstanding test waiter, if there is one, and clears it. Every release goes
     * through here because a token is not always outstanding at the point one has to be released:
     * `@renderedWithPromise` never begins the opening token, `show` clears it once the element is
     * on screen, and a dialog can raise `close` without a dismissal having begun one. Ending a
     * token that was never begun throws, and clearing it keeps a later release from ending the
     * same token twice.
     */
    settleToken() {
      if (this.token) this.waiter.endAsync(this.token);

      this.token = null;
    }

    /**
     * Latches the dismissal without running one. For a subclass whose element can be dismissed by
     * something other than `close` — a dialog closing on a `form method="dialog"` submit, say,
     * which leaves the component dismissed although `close` never ran. Without this a later
     * dismissal request would play the whole sequence against an element that is already gone and
     * report a second `onClose`.
     */
    markDismissed() {
      this.#isClosing = true;
    }

    /**
     * Reports the dismissal to the consumer, at most once for the life of the component. `close`
     * hands off to `dismissElement` only after the exit transition has played, and an element
     * dismissed by something else inside that window — a dialog shut by a `form method="dialog"`
     * submit — has already reported through whatever caught it. `dismissElement` then finds nothing
     * left to dismiss and would report the same dismissal a second time. Latching for good rather
     * than for the length of the close is safe on the same grounds as `#isClosing`: nothing reopens
     * a dismissed component in place.
     */
    reportClose() {
      if (this.#hasReportedClose) return;

      this.#hasReportedClose = true;

      this.args.onClose?.();
    }

    /**
     * Invoked by the inherited `setupComponent` modifier once the element is in the DOM. A subclass
     * overriding this calls `super.setup()` to keep the open sequence.
     */
    setup() {
      super.setup?.();

      this.show();
    }

    /**
     * Runs the open sequence. `onOpen` fires as it starts and `onShow` once `showElement` reports
     * the element is actually showing, so a consumer that measures or moves focus on `onShow` gets
     * an element genuinely on screen. The visible flip itself happens at construction — see
     * `isVisible`.
     */
    show = async () => {
      this.args.onOpen?.();

      // A consumer can dismiss straight from `onOpen`. Showing afterwards would put the element on
      // screen with the dismissal already latched, leaving nothing able to close it again.
      if (this.#isClosing) return;

      await this.showElement();

      // A dismissal arriving while `showElement` was still running has already settled the opening
      // token and put its own in `this.token`. Carrying on would settle that closing token early
      // and null the reference `dismissElement` needs, so the open sequence stands down: a
      // component on its way out is never reported as shown.
      if (this.#isClosing) return;

      // Held from construction until here rather than released as soon as the component exists, so
      // `settled()` covers the gap between the component being created and its element showing.
      this.settleToken();

      // Released first, then checked. A component torn down while `showElement` was running still
      // owns a waiter that has to be settled or nothing ever finishes waiting on it, but there is
      // no longer anything to report as shown.
      if (this.isDestroying || this.isDestroyed) return;

      this.args.onShow?.();
    };

    /**
     * public entry point for a dismissal request; waits for the result of the private
     * `#beforeClose` and continues to `close` only when a truthy response is received
     */
    beforeClose = async evt => {
      if (this.#isClosing || this.#awaitingBeforeClose) return;

      this.#awaitingBeforeClose = true;

      try {
        if (await this.#beforeClose(evt)) {
          this.close();
        }
      } finally {
        this.#awaitingBeforeClose = false;
      }
    };

    /**
     * public close function begins the closing test waiter, removes the `is-visible` class so the
     * subclass stylesheet can play its exit transition, waits that transition out where there is
     * one to play, then hands off to `dismissElement`. The wait keeps the element in the DOM long
     * enough for the transition to finish before destruction.
     */
    close = async () => {
      if (this.#isClosing) return;

      this.#isClosing = true;

      // Only outstanding when a dismissal interrupts an open still in flight, since `show` clears
      // it once the element is showing. Settling it here hands the waiter from the open sequence to
      // this one rather than stranding it.
      this.settleToken();

      this.token = this.waiter.beginAsync();
      this.isVisible = false;
      this.beginDismiss();

      await this.awaitAnimation();

      // Deliberately not guarded on `isDestroying`, unlike `show`. A consumer that unrenders this
      // component from `onBeforeClose` — persisting the dismissal before the exit transition plays —
      // destroys it inside the wait above, and it is still owed its `onClose`: the teardown is a
      // reaction to the dismissal, not a substitute for reporting one. Every `dismissElement` copes
      // with a detached element already, the modal's by falling through to the branch that settles
      // the waiter and reports directly.
      this.dismissElement();
    };

    /**
     * Dismissal request carried by the Escape key, for elements the browser does not dismiss on its
     * own. Defined here but attached by subclasses: a native modal dialog already raises `cancel`
     * for Escape, and a banner should gain no keyboard behavior at all, so only the cases that need
     * it wire this up.
     *
     * Claiming the event is what keeps nesting straight. Listeners run innermost first, so a
     * dropdown inside a flyout marks the Escape handled and the flyout's own listener leaves it
     * alone, closing one layer per keystroke rather than the whole stack at once.
     */
    handleEscape = evt => {
      if (evt.defaultPrevented || !isCloseKey(evt.key)) return;

      evt.preventDefault();

      this.beforeClose(evt);
    };
  };

/**
 * The capability's own metadata, before any component composes it. It carries what `Dismissible`
 * declares rather than the merged view, so `@animated` appears on the `Animated` page instead of
 * being restated here.
 */
export const DISMISSIBLE_CAPABILITY_METADATA = METADATA;

export default Dismissible;
